import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { PropertyVideoPhone } from "@/components/public/property-video-phone"
import { REAL_EXAMPLES, type RealExample } from "@/data/real-examples"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Before/after example card                                           */
/* ------------------------------------------------------------------ */

function ExampleCard({ example }: { example: RealExample }) {
  return (
    <div>
      {/* Property info */}
      <div className="mb-6 text-center">
        <h3 className="font-display text-2xl font-bold text-charcoal">{example.property}</h3>
        <p className="mt-1 text-sm text-slate">
          {example.location} &middot; {example.price} &middot; {example.beds} bed &middot; {example.baths} bath
        </p>
        <p className="mt-1 text-xs text-warm-grey">
          Listed by{" "}
          <a
            href={example.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-slate underline hover:text-red"
          >
            {example.agency}
          </a>
        </p>
      </div>

      {/* Side by side panels */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Listing photo panel */}
        <div className="rounded-2xl border border-stone bg-cream/50 p-5">
          <p className="mb-3 text-center text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
            Listing photos
          </p>
          <div className="overflow-hidden rounded-xl shadow-md">
            <img
              src={example.propertyImage}
              alt={`${example.property}, ${example.location}`}
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
              decoding="async"
              width={800}
              height={600}
            />
          </div>
          {/* Thumbnails */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            {example.thumbnails.map((thumb, i) => (
              <div key={i} className="overflow-hidden rounded-lg shadow-sm">
                <img
                  src={thumb}
                  alt={`${example.property} photo ${i + 2}`}
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width={200}
                  height={150}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Video ad panel */}
        <div className="rounded-2xl border border-stone bg-cream/50 p-5">
          <p className="mb-3 text-center text-xs font-medium uppercase tracking-[0.15em] text-red">
            Video ad
          </p>
          <div className="flex items-center justify-center">
            <PropertyVideoPhone
              videoUrl={example.videoUrl}
              poster={example.videoPoster}
              label={`QuiteRight video for ${example.property}`}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function Examples({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
            },
          })}
        </script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Real results</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            From images to a video ad in minutes<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate">
            Estate agents using video ads get more eyes on their listings and more
            buyers through the door. These are real property videos, created from
            listing photos in minutes. No filming. No editing.
          </p>
        </div>
      </section>

      {/* Value props */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal stagger className="mx-auto grid max-w-4xl grid-cols-2 gap-8 md:grid-cols-4">
          {[
            { stat: "2.4x", label: "More viewings booked" },
            { stat: "~3 min", label: "From listing to video" },
            { stat: "73%", label: "Of buyers watch video first" },
            { stat: "40%", label: "Faster time to offer" },
          ].map((item) => (
            <div key={item.label} className="text-center">
              <p className="font-display text-3xl font-bold text-cream md:text-4xl">
                {item.stat}
              </p>
              <p className="mt-1 text-sm text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Examples */}
      <section className="border-t border-stone bg-white px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="mb-16 text-center">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">See for yourself</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
              Real listings. Real videos<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate">
              Every video below was created from listing photos in just a few minutes.
              Same images the portal already has. No extra photography needed.
            </p>
          </ScrollReveal>

          <div className="space-y-24">
            {REAL_EXAMPLES.map((example) => (
              <ScrollReveal key={example.property}>
                <ExampleCard example={example} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why video works */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Save time. Get more viewings<span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate">
              Video marketing used to mean expensive film crews, hours of editing,
              and days of waiting. Now it takes less time than writing a portal
              description. The result? Listings that stand out, buyers that engage,
              and viewing diaries that fill up.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-14 grid gap-6 sm:grid-cols-3">
            {[
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                ),
                title: "Minutes, not days",
                description:
                  "Paste a listing URL, pick a template, get a video. The whole process takes just a few minutes. No waiting for videographers, no back-and-forth on edits.",
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                  </svg>
                ),
                title: "More eyes on every listing",
                description:
                  "Video ads reach buyers who would never scroll past a static photo. More visibility on Instagram, TikTok, Facebook, and YouTube means more enquiries landing in your inbox.",
              },
              {
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="#C41E2A" strokeWidth={1.5} className="size-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                  </svg>
                ),
                title: "Fill your viewing diary",
                description:
                  "Buyers who watch a video arrive ready to act. More serious viewings, fewer tyre-kickers, and offers that come faster. Your vendors will notice the difference.",
              },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-stone bg-white p-6">
                <div className="flex size-10 items-center justify-center rounded-lg bg-red/10">
                  {item.icon}
                </div>
                <h3 className="mt-4 font-display text-lg font-bold text-charcoal">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Your listings could look like this<span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Try it free. Paste your first listing and see the result in minutes.
            No credit card. No commitment. Just a video that gets more viewings.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Try it with your own listings
            </Link>
            <Link
              href="/how-it-works"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate/30 bg-slate/10 px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate/20 sm:w-auto"
            >
              See how it works
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            Free to try &middot; No credit card required &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
